import { Box, DialogContentText, Typography, useTheme } from '@mui/material';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { FC, useCallback, useEffect } from 'react';

import { Mailroom } from '@apiContract';
import { useDate } from '@hooks/useDate';

import { isPickUpLessCurrentDate } from '../utils';
import { useGetData } from './useGetData';

export type PickUpDate = Date | null | undefined;
export type Error = string;

interface PickupActionProps {
  mailRoom?: Mailroom;
  pickUpDate: PickUpDate;
  onChangeValue: (date: PickUpDate) => any;
  error: string;
  onChangeError: (error: string) => any;
  piecesQuantity: number;
}

export const PickupAction: FC<PickupActionProps> = ({
  mailRoom,
  pickUpDate,
  onChangeValue,
  error,
  onChangeError,
  piecesQuantity,
}) => {
  const theme = useTheme();
  const { getMailRoomWorkingHours } = useGetData(mailRoom);
  const { getDate } = useDate();

  const mailroomHasSchedule = Boolean(mailRoom?.hoursOfOperation?.activeSpans.length);

  const getTitle = () => {
    const { openDate, closeDate } = getMailRoomWorkingHours(pickUpDate);
    return (
      <>
        Please schedule a time to pick up{' '}
        {piecesQuantity > 1 ? (
          <>
            <strong>{piecesQuantity}</strong> items
          </>
        ) : (
          'this item'
        )}
        . The mailroom is operating from {getDate(openDate).timeAmPm} to {getDate(closeDate).timeAmPm} today.
      </>
    );
  };

  // true if validation error
  const validateDay = (day: Date) => {
    const mailroomConfig = getMailRoomWorkingHours(day);
    return !mailroomConfig.available;
  };

  // true if validation error
  const validateTime = useCallback(
    (hours: number, minutes: number) => {
      const mailroomConfig = getMailRoomWorkingHours(pickUpDate);
      if (!mailroomConfig.available) return true;

      const { openMinutes, closeMinutes } = mailroomConfig;
      const currentMin = hours * 60 + minutes;

      return !(openMinutes <= currentMin && closeMinutes >= currentMin);
    },
    [getMailRoomWorkingHours, pickUpDate],
  );

  const onChange = (date: PickUpDate) => {
    if (!date) return onChangeValue(date);

    const hours = date.getHours();
    const minutes = date.getMinutes();

    if (validateTime(hours, minutes) || isPickUpLessCurrentDate(date)) {
      onChangeError('This time is not available.');
    } else onChangeError('');
    onChangeValue(date);
  };

  useEffect(() => {
    if (pickUpDate && !error) {
      const hours = pickUpDate.getHours();
      const minutes = pickUpDate.getMinutes();

      if (validateTime(hours, minutes)) {
        onChangeError('This time is not available.');
      }
    }
  }, [onChangeError, error, pickUpDate, validateTime]);

  useEffect(() => {
    onChangeError(mailroomHasSchedule ? '' : 'Mailroom configuration is wrong. Please contact with your supervisor.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailroomHasSchedule]);

  return mailroomHasSchedule ? (
    <>
      <DialogContentText>{getTitle()}</DialogContentText>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <MobileDateTimePicker
          label="Pickup Time"
          value={pickUpDate}
          onChange={onChange}
          shouldDisableDate={validateDay}
          minDate={new Date()}
          slotProps={{
            textField: {
              fullWidth: true,
            },
            layout: {
              sx: error
                ? {
                    '& .MuiClock-pin': { backgroundColor: theme.palette.error.main },
                    '& .MuiClockPointer-root': {
                      backgroundColor: theme.palette.error.main,
                      '.MuiClockPointer-thumb': {
                        borderColor: theme.palette.error.main,
                        backgroundColor: theme.palette.error.main,
                      },
                    },
                  }
                : {},
            },
          }}
        />
        {Boolean(error) && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Box>
    </>
  ) : (
    <Typography color="error" sx={{ width: '100%', textAlign: 'center' }} variant="subtitle2">
      {error}
    </Typography>
  );
};
